import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import WhiteCard from '@commons/WhiteCard';

import { Textarea } from '@lib/inpulse/Textarea';

import { BlockTitle, BlockTitleIcon } from '../clientFormStyledComponents';

/**
 * When the card must match the height of a neighbouring column, the WhiteCard box and its textarea
 * are stretched to fill the available height (WhiteCard has no dedicated prop for this).
 */
const FillHeightWrapper = styled.div`
  height: 100%;

  /* WhiteCard Container */
  > div {
    height: 100%;
  }

  /* WhiteCard ButtonContainer wrapping the textarea */
  > div > div:last-child {
    flex: 1;
    min-height: 0;
  }

  /* Textarea Container */
  > div > div:last-child > div {
    height: 100%;
  }

  textarea {
    height: 100%;
    min-height: 0;
    resize: none;
  }
`;

const TEXTAREA_HEIGHT = '160px';

/**
 * Applied when the card is not stretched to a neighbouring column: the textarea keeps a fixed
 * height instead of the two rows rendered by default. Border-box so that the declared height is
 * the rendered one, paddings and borders included.
 */
const FixedHeightWrapper = styled.div`
  textarea {
    box-sizing: border-box;
    height: ${TEXTAREA_HEIGHT};
  }
`;

/**
 * "Commentaires" block shared by the external-client creation and detail pages. Optional field.
 * `isReadOnly` renders it disabled for the consultation mode of the detail page. `fillHeight`
 * stretches the card (and its textarea) to the height of its container.
 */
const ClientCommentCard = (props) => {
  const { comment, onChangeComment, isReadOnly, width, maxWidth, rows, fillHeight } = props;

  const renderContent = () => (
    <Textarea
      placeholder={i18next.t('EXTERNAL_CLIENTS.CREATION.COMMENTS_PLACEHOLDER')}
      readOnly={isReadOnly}
      rows={rows}
      value={comment == null ? '' : comment}
      onChange={(event) => onChangeComment(event.target.value)}
    />
  );

  const card = (
    <WhiteCard
      customStyle={fillHeight ? { height: '100%' } : undefined}
      maxWidth={maxWidth}
      renderContent={renderContent()}
      title={
        <BlockTitle>
          <BlockTitleIcon src={'/images/inpulse/edit-pen-black-small.svg'} />
          {i18next.t('EXTERNAL_CLIENTS.CREATION.COMMENTS')}
        </BlockTitle>
      }
      width={width}
    />
  );

  if (fillHeight) {
    return <FillHeightWrapper>{card}</FillHeightWrapper>;
  }

  return <FixedHeightWrapper>{card}</FixedHeightWrapper>;
};

ClientCommentCard.propTypes = {
  comment: PropTypes.string,
  onChangeComment: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  rows: PropTypes.number,
  fillHeight: PropTypes.bool,
};

ClientCommentCard.defaultProps = {
  comment: '',
  isReadOnly: false,
  width: '840px',
  rows: undefined,
  fillHeight: false,
};

export default ClientCommentCard;
